import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    Card,
    CardBody,
    CardFooter,
    HStack,
    IconButton,
    Image,
    Input,
    Text,
    Textarea,
    VStack,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {useMemo, useRef, useState} from "react";
import {useCatalogItemStore} from "../store/catalogItem";
import type {CatalogItem} from "../store/catalogItem";
import {Icons} from "./Icons";

type LayoutMode = "grid" | "list";

type Props = {
    item: CatalogItem;
    layout: LayoutMode;
};

export default function CatalogItemCard({item, layout}: Props) {
    const isList = layout === "list";

    const toast = useToast();

    const updateCatalogItem = useCatalogItemStore((s) => s.updateCatalogItem);
    const deleteCatalogItem = useCatalogItemStore((s) => s.deleteCatalogItem);

    const border = useColorModeValue("gray.200", "whiteAlpha.200");

    const [isEditing, setIsEditing] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const [name, setName] = useState(item.name ?? "");
    const [price, setPrice] = useState(String(item.price ?? ""));
    const [imageUrl, setImageUrl] = useState(item.image ?? "");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [description, setDescription] = useState(item.description ?? "");

    const deleteCancelRef = useRef<HTMLButtonElement>(null);

    const resetForm = (): void => {
        setName(item.name ?? "");
        setPrice(String(item.price ?? ""));
        setImageUrl(item.image ?? "");
        setImageFile(null);
        setDescription(item.description ?? "");
    };

    const hasUrl = useMemo(() => imageUrl.trim().length > 0, [imageUrl]);
    const hasFile = useMemo(() => imageFile !== null, [imageFile]);

    const imageModeError = useMemo(() => {
        if (hasUrl && hasFile) return "Choose either Image URL or file upload, not both.";
        return "";
    }, [hasUrl, hasFile]);

    const canSave = useMemo(() => {
        return !(!name.trim() || !price.trim() || imageModeError);
        
    }, [name, price, imageModeError]);

    const onSave = async (): Promise<void> => {
        if (!canSave) return;

        const priceNumber = Number(price);

        const r = await updateCatalogItem(item._id, {
            name: name.trim(),
            price: Number.isFinite(priceNumber) ? priceNumber : 0,
            image: imageUrl.trim() || undefined,
            imageFile,
            description: description.trim() || undefined,
        });

        if (!r.success) {
            toast({
                title: "Update failed",
                description: r.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        toast({
            title: "Updated",
            description: "Item updated successfully.",
            status: "success",
            duration: 2000,
            isClosable: true,
        });

        setIsEditing(false);
    };

    const onDelete = async (): Promise<void> => {
        const r = await deleteCatalogItem(item._id);

        if (!r.success) {
            toast({
                title: "Delete failed",
                description: r.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        toast({
            title: "Deleted",
            description: "Item deleted.",
            status: "success",
            duration: 2000,
            isClosable: true,
        });

        setIsDeleteOpen(false);
    };

    return (
        <Card
            w="full"
            display="flex"
            flexDirection={isList ? "row" : "column"}
            overflow={isList && !isEditing ? "hidden" : "visible"}
            transition="all 0.2s"
            _hover={{transform: "scale(1.01)", borderColor: "app.glow", shadow: "lg"}}
            {...(isList && !isEditing
                ? {alignItems: "center" as const, maxH: "140px" as const}
                : {alignItems: "stretch" as const})}
            borderColor={border}
        >
            <Image
                src={item.image ?? ""}
                alt={item.name}
                objectFit="cover"
                {...(isList
                    ? {w: "140px", h: "140px"}
                    : {w: "full", h: "220px"})}
                fallbackSrc="https://via.placeholder.com/400x220?text=No+Image"
            />

            <VStack w="full" align="stretch" spacing={0}>
                <CardBody py={isList ? 3 : 6}>
                    {!isEditing ? (
                        <VStack align="start" spacing={1}>
                            <Text fontSize="lg" fontWeight="bold">
                                {item.name}
                            </Text>
                            <Text fontSize="sm" opacity={0.8}>
                                ${item.price}
                            </Text>
                            {item.description && (
                                <Text fontSize="sm" opacity={0.85}>
                                    {item.description}
                                </Text>
                            )}
                        </VStack>
                    ) : (
                        <VStack align="stretch" spacing={3}>
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"/>
                            <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Price"/>

                            <Input
                                placeholder="Image URL"
                                value={imageUrl}
                                onChange={(e) => {
                                    setImageUrl(e.target.value);
                                    if (e.target.value) {
                                        setImageFile(null);
                                    }
                                }}
                                isDisabled={hasFile}
                            />

                            <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const f = e.target.files?.[0] ?? null;
                                    setImageFile(f);
                                    if (f) setImageUrl("");
                                }}
                                isDisabled={hasUrl}
                            />

                            {imageModeError && (
                                <Text fontSize="sm" color="red.400">
                                    {imageModeError}
                                </Text>
                            )}

                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Description (optional)"
                                resize="vertical"
                            />
                        </VStack>
                    )}
                </CardBody>

                <CardFooter py={2}>
                    {!isEditing ? (
                        <HStack spacing={2}>
                            <IconButton
                                aria-label="Edit item"
                                icon={<Icons.Edit/>}
                                size="sm"
                                variant="ghost"
                                onClick={() => setIsEditing(true)}
                            />
                            <IconButton
                                aria-label="Delete item"
                                icon={<Icons.Delete/>}
                                size="sm"
                                variant="ghost"
                                onClick={() => setIsDeleteOpen(true)}
                                colorScheme="red"
                            />
                        </HStack>
                    ) : (
                        <HStack spacing={2}>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    resetForm();
                                    setIsEditing(false);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="brand"
                                onClick={() => void onSave()}
                                isDisabled={!canSave}
                            >
                                Save
                            </Button>
                        </HStack>
                    )}
                </CardFooter>
            </VStack>

            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={deleteCancelRef}
                onClose={() => setIsDeleteOpen(false)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete item
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure? You can’t undo this action.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={deleteCancelRef} onClick={() => setIsDeleteOpen(false)}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" ml={3} onClick={() => void onDelete()}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Card>
    );
}